import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePayment } from '../context/PaymentContext';
import { useTranslation } from 'react-i18next';
import { useAllCategories } from '../hooks/useGameCategories';
import '../styles/MainPage.css';
import '../styles/GISGameStyles.css';
import { FaChevronLeft, FaChevronRight, FaSearch, FaTimes } from 'react-icons/fa';
import Preloader from './Preloader';
import GameCollections from './GameCollections';

import WinningsTicker from './WinningsTicker';
import CollectionFilters from './CollectionFilters';
import GameSlider from './GameSlider';
import '../styles/WinningsTicker.css';
import '../styles/CollectionFilters.css';
import AuthModal from './AuthModal';
import { initializeDynamicGradients, setupDynamicGradientObserver, applyDynamicGradient } from '../utils/colorExtractor';
import { initializeTextAdjustment, applyTextAdjustment, TEXT_ADJUSTMENT_PRESETS } from '../utils/textSizeAdjuster';

import { getApiUrl } from "../utils/config";
const API_URL = getApiUrl();



const GISMainPage = React.memo(() => {
    const { isAuth } = useAuth();
    const { isBalanceLoaded, isCurrencyLoaded } = usePayment();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { allCategories } = useAllCategories();
    const [games, setGames] = useState([]);
    const [popularGames, setPopularGames] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [collectionsLoading, setCollectionsLoading] = useState(false);
    const [gamesLoading, setGamesLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentBanner, setCurrentBanner] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [isSliderPaused, setIsSliderPaused] = useState(false);

    // Поиск
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const searchTimeoutRef = useRef(null);

    // Провайдеры
    const [providers, setProviders] = useState([]);
    const [selectedProvider, setSelectedProvider] = useState('');
    const [providersLoading, setProvidersLoading] = useState(false);
    const [showProviderDropdown, setShowProviderDropdown] = useState(false);
    const [providerSearchTerm, setProviderSearchTerm] = useState('');

    // Подборки
    const [selectedCollection, setSelectedCollection] = useState('');
    const [selectedSubcollection, setSelectedSubcollection] = useState('');
    const [isGISCollection, setIsGISCollection] = useState(false);
    const [subcategoriesData, setSubcategoriesData] = useState(null);

    // Мобильная версия
    const [mobileView, setMobileView] = useState('buttons'); // 'buttons', 'search', 'providers'

    // Состояние для отслеживания неудачно загруженных изображений
    const [failedImages, setFailedImages] = useState(new Set());

    // Ref для выпадающего списка провайдеров
    const providerDropdownRef = useRef(null);

    // Состояние для модального окна авторизации
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [showBonusInfoModal, setShowBonusInfoModal] = useState(false);

    // Данные для баннеров
    const bannerImages = useMemo(() => [
        {
            src: "/assets/banners/bonuses.png",
            alt: "Бонус +500%",
            title: "Бонус +500%",
            subtitle: "Играй и выигрывай с лучшими слотами",
            isBonusBanner: true // Специальный флаг для обработки текста
        },
        {
            src: "/assets/banners/kashback.png",
            alt: "Кэшбэк",
            title: "Кэшбэк",
            subtitle: "Получай фриспины и кэшбэк каждый день",
            isCashbackBanner: true // Специальный флаг для обработки текста
        }
    ], []);

    const staticBanners = useMemo(() => [
        {
            src: "/assets/banners/rakeback.webp",
            alt: "Rakeback",
            title: "",
            isRakebackBanner: true // Специальный флаг для обработки клика
        },
        {
            src: "/assets/banners/refshar.webp",
            alt: "Ref Program",
            title: "",
            isReferralBanner: true // Специальный флаг для обработки клика
        }
    ], []);

    // Функции для управления слайдером
    const nextBanner = useCallback(() => {
        setCurrentBanner((prev) => (prev + 1) % bannerImages.length);
    }, [bannerImages.length]);

    const prevBanner = useCallback(() => {
        setCurrentBanner((prev) => (prev - 1 + bannerImages.length) % bannerImages.length);
    }, [bannerImages.length]);

    // Функция обработки клика на баннеры promop1.png и promop2.png
    const handleBonusBannerClick = useCallback((e, bannerIndex) => {
        // Проверяем, что это первый или второй баннер (promop1.png или promop2.png)
        if (bannerIndex === 0 || bannerIndex === 1) {
            e.preventDefault(); // Предотвращаем стандартную навигацию Link

            if (isAuth) {
                // Авторизованный пользователь - перенаправляем на FreeMoney
                navigate('/freemoney');

                // Определяем, какой баннер открыть на странице FreeMoney
                setTimeout(() => {
                    if (bannerIndex === 0) {
                        // promop1.png - открываем баннер "Бонус +500%"
                        const bonusElement = document.getElementById('banner-bonus-500');
                        if (bonusElement) {
                            bonusElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            bonusElement.click(); // Открываем модальное окно
                        }
                    } else if (bannerIndex === 1) {
                        // promop2.png - открываем баннер "Кэшбэк и бонусы"
                        const cashbackElement = document.getElementById('banner-cashback');
                        if (cashbackElement) {
                            cashbackElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            cashbackElement.click(); // Открываем модальное окно
                        }
                    }
                }, 500); // Увеличиваем задержку для полной загрузки страницы
            } else {
                // Неавторизованный пользователь - показываем модальное окно регистрации
                setShowAuthModal(true);
            }
        }
    }, [isAuth, navigate]);

    // Функция обработки клика на баннер Rakeback
    const handleRakebackBannerClick = useCallback((e) => {
        e.preventDefault(); // Предотвращаем стандартную навигацию Link

        if (isAuth) {
            // Авторизованный пользователь - перенаправляем на FreeMoney к баннеру Rakeback
            navigate('/freemoney');

            // Небольшая задержка для загрузки страницы, затем прокручиваем к баннеру rakeback
            setTimeout(() => {
                const rakebackElement = document.getElementById('banner-rakeback');
                if (rakebackElement) {
                    rakebackElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    rakebackElement.click(); // Открываем модальное окно
                }
            }, 500);
        } else {
            // Неавторизованный пользователь - показываем модальное окно регистрации
            setShowAuthModal(true);
        }
    }, [isAuth, navigate]);

    // Функция обработки клика на баннер Referral Program
    const handleReferralBannerClick = useCallback((e) => {
        e.preventDefault(); // Предотвращаем стандартную навигацию Link

        if (isAuth) {
            // Авторизованный пользователь - перенаправляем на страницу Referral Program
            navigate('/referral');
        } else {
            // Неавторизованный пользователь - показываем модальное окно регистрации
            setShowAuthModal(true);
        }
    }, [isAuth, navigate]);

    const handleSliderMouseEnter = useCallback(() => {
        setIsSliderPaused(true);
    }, []);

    const handleSliderMouseLeave = useCallback(() => {
        setIsSliderPaused(false);
    }, []);

    // Функция для получения игр
    const fetchGames = useCallback(async (page = 1, resetGames = true, searchQuery = '', providerFilter = '', collectionFilter = '', subcollectionFilter = '') => {
        try {
            if (resetGames) {
                setLoading(true);
                setGamesLoading(true);
                setError(null);
                setFailedImages(new Set()); // Очищаем список неудачных изображений при новой загрузке
            } else {
                setLoadingMore(true);
            }

            // Если выбрана подборка, получаем игры из подборки
            if (collectionFilter) {
                let endpoint = `${API_URL}/api/collections/${collectionFilter}`;
                if (subcollectionFilter) {
                    endpoint += `/${subcollectionFilter}`;
                }

                // Параметры пагинации для подборок
                const params = new URLSearchParams();
                params.set('limit', '50');
                params.set('offset', resetGames ? '0' : String(games.length));

                const response = await fetch(`${endpoint}?${params.toString()}`, {
                    credentials: 'include'
                });

                if (!response.ok) {
                    throw new Error('Ошибка загрузки подборки');
                }

                const data = await response.json();

                if (data.success && data.collection) {
                    setIsGISCollection(false);

                    // Проверяем, есть ли подкатегории
                    if (data.collection.hasSubcategories && data.collection.subcategories) {
                        setSubcategoriesData(data.collection.subcategories);
                        setGames([]); // Очищаем игры, так как будем показывать слайдеры
                        setCurrentPage(1);
                        setTotalPages(1);
                        setTotalCount(0);
                    } else {
                        setSubcategoriesData(null);
                        const newItems = data.collection.games || [];
                        if (resetGames) {
                            setGames(newItems);
                            setCurrentPage(1);
                        } else {
                            setGames(prev => [...prev, ...newItems]);
                        }
                        setTotalPages(1); // для коллекций используем offset/limit
                        if (typeof data.collection.total === 'number') {
                            setTotalCount(data.collection.total);
                        } else {
                            setTotalCount(resetGames ? newItems.length : games.length + newItems.length);
                        }
                    }
                }
            } else {
                // Обычная загрузка игр через GIS API
                setIsGISCollection(false);
                setSubcategoriesData(null);
                const params = new URLSearchParams({
                    page: page.toString(),
                    per_page: '50',
                    expand: 'tags,parameters,images'
                });

                // Добавляем поисковый запрос если есть
                if (searchQuery && searchQuery.trim()) {
                    params.append('search', searchQuery.trim());
                }

                // Добавляем фильтр по провайдеру если есть
                if (providerFilter && providerFilter.trim()) {
                    params.append('provider', providerFilter.trim());
                }

                const response = await fetch(`${API_URL}/api/gis/games?${params}`, {
                    credentials: 'include'  // Для отправки cookies если пользователь авторизован
                });

                if (!response.ok) {
                    console.error('❌ HTTP Error:', response.status, response.statusText);
                    throw new Error(`Ошибка загрузки игр: HTTP ${response.status}`);
                }

                const data = await response.json();

                if (resetGames) {
                    const baseItems = data.items || [];
                    const shouldShuffle = !searchQuery && !providerFilter && !collectionFilter;
                    const items = shouldShuffle ? [...baseItems].sort(() => Math.random() - 0.5) : baseItems;
                    setGames(items);
                    setCurrentPage(1);
                } else {
                    setGames(prevGames => [...prevGames, ...(data.items || [])]);
                }

                // Получаем информацию о пагинации из заголовков или метаданных
                const pageCount = response.headers.get('X-Pagination-Page-Count');
                if (pageCount) {
                    setTotalPages(parseInt(pageCount));
                } else if (data._meta && data._meta.pageCount) {
                    setTotalPages(data._meta.pageCount);
                }

                // Получаем общее количество игр
                const totalCountHeader = response.headers.get('X-Pagination-Total-Count');
                if (totalCountHeader) {
                    setTotalCount(parseInt(totalCountHeader));
                } else if (data._meta && data._meta.totalCount) {
                    setTotalCount(data._meta.totalCount);
                }

                setCurrentPage(page);
            }
        } catch (err) {
            console.error('Ошибка при загрузке игр:', err);

            // Более детальная информация об ошибке
            let errorMessage = 'Не удалось загрузить список игр';
            if (err.message.includes('HTTP')) {
                errorMessage += ` (${err.message})`;
            } else if (err.name === 'TypeError' && err.message.includes('fetch')) {
                errorMessage = 'Ошибка соединения с сервером';
            }

            setError(errorMessage);
        } finally {
            setLoading(false);
            setLoadingMore(false);
            setGamesLoading(false);
        }
    }, []);

    // Функция для получения провайдеров
    const fetchProviders = useCallback(async () => {
        try {
            setProvidersLoading(true);
            const response = await fetch(`${API_URL}/api/gis/games/providers`, {
                credentials: 'include'  // Для отправки cookies если пользователь авторизован
            });

            if (!response.ok) {
                console.error('❌ Providers HTTP Error:', response.status, response.statusText);
                throw new Error(`Ошибка загрузки провайдеров: HTTP ${response.status}`);
            }

            const data = await response.json();
            setProviders(data.providers || []);
            console.log('✅ Провайдеры загружены:', data.providers?.length || 0);
        } catch (err) {
            console.error('Ошибка при загрузке провайдеров:', err);
            // Не показываем ошибку пользователю, просто оставляем пустой список
        } finally {
            setProvidersLoading(false);
        }
    }, []);

    // Функция для получения популярных игр
    const fetchPopularGames = useCallback(async () => {
        try {
            setCollectionsLoading(true);
            const response = await fetch(`${API_URL}/api/collections`, {
                credentials: 'include'
            });

            if (!response.ok) {
                console.error('❌ Collections HTTP Error:', response.status, response.statusText);
                throw new Error(`Ошибка загрузки коллекций: HTTP ${response.status}`);
            }

            const data = await response.json();

            if (data.success && data.collections && data.collections.popular) {
                setPopularGames(data.collections.popular.games || []);
                console.log('✅ Популярные игры загружены:', data.collections.popular.games?.length || 0);
            }
        } catch (err) {
            console.error('Ошибка при загрузке популярных игр:', err);
            // Не показываем ошибку пользователю для популярных игр
        } finally {
            setCollectionsLoading(false);
        }
    }, []);

    // Инициализация данных
    useEffect(() => {
        fetchGames();
        fetchProviders();
        fetchPopularGames();
    }, [fetchGames, fetchProviders, fetchPopularGames]);

    // Инициализация динамических градиентов и текстовой адаптации
    useEffect(() => {
        // Инициализируем градиенты после загрузки игр
        const timer = setTimeout(() => {
            initializeDynamicGradients();

            // Инициализируем адаптацию текста для названий игр
            initializeTextAdjustment('.universal-game-name', TEXT_ADJUSTMENT_PRESETS.gameName);
            initializeTextAdjustment('.universal-game-provider', TEXT_ADJUSTMENT_PRESETS.gameProvider);
            initializeTextAdjustment('.universal-game-placeholder-title', TEXT_ADJUSTMENT_PRESETS.placeholder);
        }, 100);

        // Настраиваем наблюдатель для новых карточек
        const observer = setupDynamicGradientObserver();

        return () => {
            clearTimeout(timer);
            if (observer) {
                observer.disconnect();
            }
        };
    }, [games]);

    // Обработка URL параметров для автоматического выбора подборки
    useEffect(() => {
        const collectionParam = searchParams.get('collection');
        if (collectionParam && collectionParam !== selectedCollection) {
            setSelectedCollection(collectionParam);
            setSelectedSubcollection('');
            setSelectedProvider('');
            // Очищаем параметр из URL после установки
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.delete('collection');
            setSearchParams(newSearchParams, { replace: true });
        }
    }, [searchParams, selectedCollection, setSearchParams]);

    // Эффект для смены баннеров
    useEffect(() => {
        if (!isSliderPaused) {
            const interval = setInterval(nextBanner, 5000);
            return () => clearInterval(interval);
        }
    }, [nextBanner, isSliderPaused]);

    // Дебаунс для поиска
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 300);

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchTerm]);

    // Обновление игр при изменении поискового запроса, провайдера или подборки
    useEffect(() => {
        fetchGames(1, true, debouncedSearchTerm, selectedProvider, selectedCollection, selectedSubcollection);
    }, [debouncedSearchTerm, selectedProvider, selectedCollection, selectedSubcollection, fetchGames]);

    // Функция для загрузки дополнительных игр
    const loadMoreGames = () => {
        // Обычный каталог (постранично)
        if (!selectedCollection && !subcategoriesData && currentPage < totalPages && !loadingMore) {
            fetchGames(currentPage + 1, false, debouncedSearchTerm, selectedProvider, selectedCollection, selectedSubcollection);
            return;
        }
        // Подборки (offset/limit)
        if (selectedCollection && !subcategoriesData && !loadingMore) {
            fetchGames(1, false, debouncedSearchTerm, selectedProvider, selectedCollection, selectedSubcollection);
        }
    };

    // Обработчики поиска
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const clearSearch = () => {
        setSearchTerm('');
    };

    // Обработчики провайдера
    const handleProviderSelect = (provider) => {
        setSelectedProvider(provider);
        setShowProviderDropdown(false);
        // Сбрасываем выбор подборки при выборе провайдера
        if (provider) {
            setSelectedCollection('');
            setSelectedSubcollection('');
        }
        // Закрываем мобильное представление если оно активно
        if (mobileView === 'providers') {
            setMobileView('buttons');
        }
    };

    const toggleProviderDropdown = () => {
        setShowProviderDropdown(!showProviderDropdown);
        if (!showProviderDropdown) {
            // Очищаем поиск при открытии
            setProviderSearchTerm('');
        }
    };

    const closeProviderDropdown = () => {
        setShowProviderDropdown(false);
    };

    const clearProvider = () => {
        setSelectedProvider('');
        setShowProviderDropdown(false);
        // Закрываем мобильное представление если оно активно
        if (mobileView === 'providers') {
            setMobileView('buttons');
        }
    };

    // Фильтрация провайдеров по поисковому запросу
    const filteredProviders = providers.filter(provider =>
        provider.toLowerCase().includes(providerSearchTerm.toLowerCase())
    );

    // Обработчики поиска провайдеров
    const handleProviderSearchChange = (e) => {
        setProviderSearchTerm(e.target.value);
    };

    const clearProviderSearch = () => {
        setProviderSearchTerm('');
    };

    // Закрытие выпадающего списка при клике вне его
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (providerDropdownRef.current && !providerDropdownRef.current.contains(event.target)) {
                setShowProviderDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const getGameImage = useCallback((game) => {
        // Бэкенд уже оптимизировал и вернул лучшее изображение в game.image
        if (!game.image || game.image === '') return null;
        
        // Если путь начинается с / - это серверное изображение, добавляем API_URL
        if (game.image.startsWith('/')) {
            return `${API_URL}${game.image}`;
        }
        
        // Иначе это уже полный URL
        return game.image;
    }, []);

    // Функция для обработки ошибки загрузки изображения
    const handleImageError = useCallback((gameUuid) => {
        setFailedImages(prev => new Set([...prev, gameUuid]));
    }, []);

    // Функция для обработки успешной загрузки изображения
    const handleImageLoad = useCallback((event, game) => {
        const img = event.target;
        const card = img.closest('.universal-game-card');
        if (card && img) {
            // 1) Устанавливаем динамический градиент по цвету изображения
            applyDynamicGradient(card, img);

            // 2) Определяем ориентацию изображения и добавляем класс для верхнего градиента только для горизонтальных
            const container = card.querySelector('.universal-game-image-container');
            if (container && img.naturalWidth && img.naturalHeight) {
                const isHorizontal = img.naturalWidth >= img.naturalHeight; // горизонтальные и квадратные
                if (isHorizontal) {
                    container.classList.add('has-top-gradient');
                } else {
                    container.classList.remove('has-top-gradient');
                }
            }

            // 3) Применяем текстовую адаптацию к элементам новой карточки
            const gameName = card.querySelector('.universal-game-name');
            const gameProvider = card.querySelector('.universal-game-provider');
            if (gameName) applyTextAdjustment(gameName, TEXT_ADJUSTMENT_PRESETS.gameName);
            if (gameProvider) applyTextAdjustment(gameProvider, TEXT_ADJUSTMENT_PRESETS.gameProvider);
        }
    }, []);



    // Обработчики мобильной версии
    const showMobileSearch = () => {
        setMobileView('search');
    };

    const showMobileProviders = () => {
        setMobileView('providers');
    };

    const closeMobileView = () => {
        setMobileView('buttons');
    };

    // Обработчики подборок
    const handleCollectionChange = (collection) => {
        setSelectedCollection(collection);
        if (!collection) {
            setSelectedSubcollection('');
            setSubcategoriesData(null);
        } else {
            // Сбрасываем выбор провайдера при выборе подборки
            setSelectedProvider('');
        }
    };

    const handleSubcollectionChange = (subcollection) => {
        setSelectedSubcollection(subcollection);
    };

    // Функции для получения названий подборок из централизованного источника
    const getCollectionTitle = (collectionKey) => {
        const category = allCategories.find(cat => cat.key === collectionKey);
        return category ? t(category.titleKey, category.key) : collectionKey;
    };

    const getSubcollectionTitle = (collectionKey, subcollectionKey) => {
        const subcategory = allCategories.find(cat => cat.key === subcollectionKey);
        return subcategory ? t(subcategory.titleKey, subcategory.key) : subcollectionKey;
    };



    if (loading && games.length === 0) {
        return <Preloader isLoading={true} />;
    }

    // Индикатор загрузки игр
    if (gamesLoading && games.length === 0) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Загрузка игр...</p>
            </div>
        );
    }

    if (error) {
        return <div className="error">{error}</div>;
    }

    return (
        <div className="mainpage">



            <div className="hero-banners">
                <div className="main-banner-container">
                    <div
                        className="banner-slider"
                        onMouseEnter={handleSliderMouseEnter}
                        onMouseLeave={handleSliderMouseLeave}
                    >
                        <div
                            className="banner-track"
                            style={{
                                transform: `translateX(-${currentBanner * (100 / bannerImages.length)}%)`,
                                width: `${bannerImages.length * 100}%`
                            }}
                        >
                            {bannerImages.map((banner, index) => (
                                <div
                                    key={index}
                                    className="banner-slide"
                                    style={{
                                        flex: `0 0 ${100 / bannerImages.length}%`,
                                        width: `${100 / bannerImages.length}%`
                                    }}
                                >
                                    <Link
                                        to={banner.link || '#'}
                                        className="banner-link"
                                        style={{ position: 'relative', display: 'block' }}
                                        onClick={(e) => handleBonusBannerClick(e, index)}
                                    >
                                        <img
                                            src={banner.src}
                                            alt={banner.alt}
                                            className="banner-image"
                                        />
                                        {banner.isBonusBanner && (
                                            <div className="gis-bonus-banner-text-overlay">
                                                <div className="gis-bonus-banner-main-text">БОНУС +500%</div>
                                                <div className="gis-bonus-banner-sub-text">НА ПЕРВЫЕ 4 ДЕПОЗИТА</div>
                                                <div className="gis-bonus-banner-action-text">ЗАБРАТЬ</div>
                                            </div>
                                        )}
                                        {banner.isCashbackBanner && (
                                            <div className="gis-cashback-banner-text-overlay">
                                                <div className="gis-cashback-banner-main-text">КЭШБЭК</div>
                                                <div className="gis-cashback-banner-main-text2">ДО 30%</div>
                                                <div className="gis-cashback-banner-sub-text">БЕЗ ОТЫГРЫША</div>
                                                <div className="gis-cashback-banner-sub-text2">СРАЗУ НА СЧЁТ</div>
                                                <div className="gis-cashback-banner-action-text">ПОЛУЧИТЬ</div>
                                            </div>
                                        )}
                                    </Link>
                                </div>
                            ))}
                        </div>

                        <div className="banner-nav-buttons">
                            <button
                                className="banner-nav-button prev"
                                onClick={prevBanner}
                                aria-label="Предыдущий баннер"
                            >
                                <FaChevronLeft />
                            </button>

                            <button
                                className="banner-nav-button next"
                                onClick={nextBanner}
                                aria-label="Следующий баннер"
                            >
                                <FaChevronRight />
                            </button>
                        </div>
                    </div>
                </div>

                <div className="side-banners">
                    {staticBanners.map((banner, index) => (
                        <div key={index} className="side-banner" style={{ position: 'relative' }}>
                            <Link
                                to={banner.link || '#'}
                                className="side-banner-link"
                                style={{ position: 'relative', display: 'block' }}
                                onClick={
                                    banner.isRakebackBanner ? handleRakebackBannerClick :
                                        banner.isReferralBanner ? handleReferralBannerClick :
                                            undefined
                                }
                            >
                                <img
                                    src={banner.src}
                                    alt={banner.alt}
                                    className="side-banner-image"
                                />
                                <div className="side-banner-overlay">
                                    <h3 className="side-banner-title">{banner.title}</h3>
                                </div>
                                {banner.isRakebackBanner && (
                                    <div className="gis-rakeback-banner-text-overlay">
                                        <div className="gis-rakeback-banner-main-text">ЕЖЕДНЕВНЫЙ</div>
                                        <div className="gis-rakeback-banner-main-text2">RAKEBACK</div>
                                        <div className="gis-rakeback-banner-sub-text">СРАЗУ НА СЧЁТ</div>
                                        <div className="gis-rakeback-banner-sub-text2">БЕЗ ОТЫГРЫША</div>
                                        <div className="gis-rakeback-banner-action-text">ПОЛУЧИТЬ</div>
                                    </div>
                                )}
                                {banner.isReferralBanner && (
                                    <div className="gis-referral-banner-text-overlay">
                                        <div className="gis-referral-banner-main-text">РЕФЕРАЛЬНАЯ</div>
                                        <div className="gis-referral-banner-main-text2">ПРОГРАММА</div>
                                        <div className="gis-referral-banner-sub-text">СТАНЬТЕ ПАРТНЕРОМ</div>
                                        <div className="gis-referral-banner-sub-text2">ПОЛУЧАЙТЕ <span className="gis-referral-banner-percent">ДО 60%</span></div>
                                        <div className="gis-referral-banner-action-text">ПРИСОЕДИНИТЬСЯ</div>
                                    </div>
                                )}
                            </Link>
                        </div>
                    ))}
                </div>
            </div>

            {/* Бегущая строка выигрышей */}
            <WinningsTicker availableGames={popularGames} />


            <div className="search-section">


                {/* Десктопная версия */}
                <div className="search-container desktop-search">
                    <div className="search-input-wrapper">
                        <FaSearch className="search-icon" />
                        <input
                            type="text"
                            placeholder="Поиск"
                            value={searchTerm}
                            onChange={handleSearchChange}
                            className="search-input"
                        />
                        {searchTerm && (
                            <button onClick={clearSearch} className="search-clear-button">
                                <FaTimes />
                            </button>
                        )}
                    </div>

                    <div className="provider-select-wrapper" ref={providerDropdownRef}>
                        <button
                            onClick={toggleProviderDropdown}
                            className="provider-select"
                            disabled={providersLoading}
                        >
                            Все провайдеры
                        </button>
                        {showProviderDropdown && (
                            <div className="provider-dropdown-list">
                                <div className="provider-search-container">
                                    <div className="provider-search-input-wrapper">
                                        <FaSearch className="provider-search-icon" />
                                        <input
                                            type="text"
                                            placeholder="Поиск провайдера..."
                                            value={providerSearchTerm}
                                            onChange={handleProviderSearchChange}
                                            className="provider-search-input"
                                            autoFocus
                                        />
                                        {providerSearchTerm && (
                                            <button onClick={clearProviderSearch} className="provider-search-clear">
                                                <FaTimes />
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className="provider-dropdown-scroll">
                                    {!providerSearchTerm && (
                                        <div
                                            className={`provider-dropdown-item ${selectedProvider === '' ? 'selected' : ''}`}
                                            onClick={() => handleProviderSelect('')}
                                        >
                                            <span>Все провайдеры</span>
                                        </div>
                                    )}
                                    {filteredProviders.map(provider => (
                                        <div
                                            key={provider}
                                            className={`provider-dropdown-item ${selectedProvider === provider ? 'selected' : ''}`}
                                            onClick={() => handleProviderSelect(provider)}
                                        >
                                            <span>{provider}</span>
                                        </div>
                                    ))}
                                    {filteredProviders.length === 0 && providerSearchTerm && (
                                        <div className="provider-dropdown-item no-results">
                                            <span>Провайдеры не найдены</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Мобильная версия */}
                <div className="mobile-search-container">
                    {mobileView === 'buttons' && (
                        <div className="mobile-buttons">
                            <button onClick={showMobileSearch} className="mobile-search-button">
                                <FaSearch />
                                <span>Поиск</span>
                            </button>
                            <button onClick={showMobileProviders} className="mobile-providers-button">
                                <span>Провайдеры</span>
                            </button>
                        </div>
                    )}

                    {mobileView === 'search' && (
                        <div className="mobile-search-active">
                            <div className="search-container mobile-search">
                                <div className="search-input-wrapper">
                                    <FaSearch className="search-icon" />
                                    <input
                                        type="text"
                                        placeholder="Поиск"
                                        value={searchTerm}
                                        onChange={handleSearchChange}
                                        className="search-input"
                                        autoFocus
                                    />
                                    {searchTerm && (
                                        <button onClick={clearSearch} className="search-clear-button">
                                            <FaTimes />
                                        </button>
                                    )}
                                </div>
                                <button onClick={closeMobileView} className="mobile-close-button">
                                    <FaTimes />
                                </button>
                            </div>
                        </div>
                    )}

                    {mobileView === 'providers' && (
                        <div className="mobile-providers-active">
                            <div className="search-container mobile-providers">
                                <div className="provider-select-wrapper" ref={providerDropdownRef}>
                                    <button
                                        onClick={toggleProviderDropdown}
                                        className="provider-select"
                                        disabled={providersLoading}
                                    >
                                        Все провайдеры
                                    </button>
                                    {showProviderDropdown && (
                                        <div className="provider-dropdown-list">
                                            <div className="provider-search-container">
                                                <div className="provider-search-input-wrapper">
                                                    <FaSearch className="provider-search-icon" />
                                                    <input
                                                        type="text"
                                                        placeholder="Поиск провайдера..."
                                                        value={providerSearchTerm}
                                                        onChange={handleProviderSearchChange}
                                                        className="provider-search-input"
                                                        autoFocus
                                                    />
                                                    {providerSearchTerm && (
                                                        <button onClick={clearProviderSearch} className="provider-search-clear">
                                                            <FaTimes />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="provider-dropdown-scroll">
                                                {!providerSearchTerm && (
                                                    <div
                                                        className={`provider-dropdown-item ${selectedProvider === '' ? 'selected' : ''}`}
                                                        onClick={() => handleProviderSelect('')}
                                                    >
                                                        <span>Все провайдеры</span>
                                                    </div>
                                                )}
                                                {filteredProviders.map(provider => (
                                                    <div
                                                        key={provider}
                                                        className={`provider-dropdown-item ${selectedProvider === provider ? 'selected' : ''}`}
                                                        onClick={() => handleProviderSelect(provider)}
                                                    >
                                                        <span>{provider}</span>
                                                    </div>
                                                ))}
                                                {filteredProviders.length === 0 && providerSearchTerm && (
                                                    <div className="provider-dropdown-item no-results">
                                                        <span>Провайдеры не найдены</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <button onClick={closeMobileView} className="mobile-close-button">
                                    <FaTimes />
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Фильтры подборок */}
                <CollectionFilters
                    selectedCollection={selectedCollection}
                    onCollectionChange={handleCollectionChange}
                    selectedSubcollection={selectedSubcollection}
                    onSubcollectionChange={handleSubcollectionChange}
                />
            </div>

            <div className="content">



                {/* Подборки игр - показываем только если нет поиска, выбранного провайдера и подборки */}
                {!debouncedSearchTerm && !selectedProvider && !selectedCollection && (!isAuth || (isBalanceLoaded && isCurrencyLoaded)) && (
                    <GameCollections loading={collectionsLoading} />
                )}

                {/* Заголовок с выбранным провайдером */}
                {selectedProvider && !selectedCollection && (
                    <h2 className="selected-provider-header">{selectedProvider}</h2>
                )}

                {/* Заголовок с выбранной подборкой */}
                {selectedCollection && (
                    <h2 className="selected-collection-header">
                        {selectedSubcollection ?
                            `${getCollectionTitle(selectedCollection)} - ${getSubcollectionTitle(selectedCollection, selectedSubcollection)}` :
                            getCollectionTitle(selectedCollection)
                        }
                    </h2>
                )}

                {/* Заголовок "Все игры" когда показываем полный каталог */}
                {!selectedProvider && !selectedCollection && !debouncedSearchTerm && (
                    <h2 className="all-games-header">
                        <img src="/assets/banners/icon1.webp" alt="Игры" className="all-games-icon" />
                        Все игры
                    </h2>
                )}

                {/* Заголовок результатов поиска */}
                {debouncedSearchTerm && (
                    <h2 className="search-results-header">
                        Результаты поиска: "{debouncedSearchTerm}"
                    </h2>
                )}

                {/* Слайдеры подкатегорий */}
                {subcategoriesData && Object.keys(subcategoriesData).length > 0 && (
                    <div className="subcategories-sliders">
                        {Object.entries(subcategoriesData).map(([subKey, subData]) => (
                            <GameSlider
                                key={subKey}
                                title={subData.title}
                                games={subData.games}
                                collectionType={subKey}
                            />
                        ))}
                    </div>
                )}

                <div className="games-grid">
                    {games.map((game) => (
                        <Link
                            key={game.uuid}
                            to={`/gis-game/${game.uuid}${!isAuth ? '?demo=true' : ''}${isAuth ? '?' : '&'}name=${encodeURIComponent(game.name)}`}
                            className="universal-game-card"
                        >
                            <div className="universal-game-image-container">
                                {failedImages.has(game.uuid) || !getGameImage(game) ? (
                                    <div className="universal-game-placeholder">
                                        <span className="universal-game-placeholder-title">{game.name}</span>
                                        <span className="universal-game-placeholder-provider">{game.provider}</span>
                                    </div>
                                ) : (
                                    <>
                                        <img
                                            src={getGameImage(game)}
                                            alt={game.name}
                                            className="universal-game-image"
                                            loading="lazy"
                                            onError={() => handleImageError(game.uuid)}
                                            onLoad={(e) => handleImageLoad(e, game)}
                                        />
                                        <div className="universal-game-info">
                                            <h3 className="universal-game-name">{game.name}</h3>
                                            <div className="universal-game-provider">{game.provider}</div>
                                        </div>
                                    </>
                                )}
                                {game.has_lobby === 1 && (
                                    <div className="universal-game-badge">Лобби</div>
                                )}
                                {game.has_freespins === 1 && (
                                    <div className="universal-game-badge freespins">Фриспины</div>
                                )}
                            </div>
                        </Link>
                    ))}
                </div>

                {/* Кнопка "Показать ещё" - для обычного каталога и обычных коллекций */}
                {currentPage < totalPages && !loading && !selectedCollection && !subcategoriesData && (
                    <div className="load-more-container">
                        <div className="games-count-bottom">
                            {totalCount > 0 ?
                                `Показано: ${games.length} из ${totalCount}` :
                                `Загружено: ${games.length}`
                            }
                        </div>
                        <button
                            onClick={loadMoreGames}
                            disabled={loadingMore}
                            className="load-more-button"
                        >
                            {loadingMore ? 'Загрузка...' : 'Показать ещё'}
                        </button>
                    </div>
                )}

                {/* Пагинация для страниц подборок (кроме подкатегорий) */}
                {selectedCollection && !subcategoriesData && (
                    <div className="load-more-container">
                        <div className="games-count-bottom">
                            {totalCount > 0 ? `Показано: ${games.length} из ${totalCount}` : `Загружено: ${games.length}`}
                        </div>
                        {(totalCount === 0 || games.length < totalCount) && (
                            <button
                                onClick={loadMoreGames}
                                disabled={loadingMore}
                                className="load-more-button"
                            >
                                {loadingMore ? 'Загрузка...' : 'Показать ещё'}
                            </button>
                        )}
                    </div>
                )}

                {/* Модальное окно авторизации */}
                {showAuthModal && (
                    <AuthModal
                        onClose={() => setShowAuthModal(false)}
                        isLoginType={false}
                    />
                )}
            </div>
        </div>
    );
});

export default GISMainPage; 